import { useState } from 'react';

import { useUploadPaylinkCustomersMutation } from '../../../store/modules/merchant/api';
import { UploadPaylinkCustomersBody } from '../../../store/modules/merchant/types';

type UseCustomerUploadProps = {
  merchantId: string;
};

type FileErrorResponse = {
  status: number;
  data: {
    file: string;
  };
};

type FileErrorResponseParsed = {
  statusCode: number;
  message: string;
  error: string;
};

const isFileError = (err: unknown): err is FileErrorResponse =>
  typeof err === 'object' &&
  err !== null &&
  'data' in err &&
  typeof err.data === 'object' &&
  err.data !== null &&
  'file' in err.data;

export const useCustomersUpload = ({ merchantId }: UseCustomerUploadProps) => {
  const [uploadPaylinkCustomers, { isLoading: isLoadingUploadCustomers }] =
    useUploadPaylinkCustomersMutation();

  const [serverValidationError, setServerValidationError] = useState<string | undefined>(undefined);

  const onCustomerUpload = async (uploadCustomersBody: UploadPaylinkCustomersBody) => {
    setServerValidationError(undefined);

    const formData = new FormData();
    formData.append('file', uploadCustomersBody.file);
    formData.append('overwrite', `${uploadCustomersBody.shouldOverwrite}`);

    try {
      return await uploadPaylinkCustomers({
        body: formData,
        merchantId,
      }).unwrap();
    } catch (err) {
      if (isFileError(err) && err.status === 400) {
        const errorMessage = (JSON.parse(err.data.file) as FileErrorResponseParsed).message;
        setServerValidationError(errorMessage);
        throw new Error(errorMessage);
      }
      const errorMessage = 'Something went wrong while uploading your file. Please try again.';
      setServerValidationError(errorMessage);
      throw err;
    }
  };

  return {
    onCustomerUpload,
    isLoading: isLoadingUploadCustomers,
    uploadValidationError: serverValidationError,
  };
};
